import type { DomainLogEntry, OutboxRow, OutboxTrigger } from '../../types';
import { OUTBOX_TRIGGER } from '../../types';
import { MATCH_STATE, transition } from '../machine';
import type { TransitionEffect, TransitionEvent } from '../machine';
import { ok, reject } from '../result';
import type {
  CreateMatchCommand,
  CreateMatchSnapshot,
  Decision,
  Postcondition,
  TransitionCommand,
  TransitionOp,
  TransitionSnapshot,
} from './types';

/**
 * SES-1 — the lifecycle commands, as pure handlers over `domain/machine.ts`.
 *
 * This file adds **no** rules. `machine.transition` owns every guard and every anchor decision; this
 * layer translates its `TransitionEffect` into the row ops the bus applies, and nothing else. That
 * split is deliberate: a second place that decides *whether* a transition is legal is a second place
 * for the transition table to drift from §2.2.
 *
 * ── What this handler is responsible for, precisely ──────────────────────────
 *
 * 1. **Carrying `anchorTs` through untouched.** SES-1 (rev 2): *"The anchor timestamp MUST be the
 *    moment the press began, not the moment the hold completed."* The command carries it from
 *    `pointerdown`; `machine.transition` validates it against `now`; nothing here recomputes it.
 *    `domain/`'s lint ban on `Date` means a handler *cannot* sample a clock even by accident, which
 *    is the mechanism behind the requirement rather than a note asking people to be careful.
 * 2. **Emitting the same-transaction side effects §2.2's rightmost column names** — the anchor, the
 *    `abandoned` flag, `triagePassedTs`, the `activeMatch` pointer, and the SYN-1 outbox row.
 * 3. **Refreshing the §13.7 writer heartbeat on every transition**, including the ones that leave the
 *    pointer alone.
 */

const MODULE = 'commands/transition';

/**
 * SYN-1's three push triggers, keyed off the event that fires them.
 *
 * `machine.TRANSITION_TABLE` decides *whether* a row is written (`row.outbox`); this decides which
 * `trigger` it carries. Keeping the map here rather than in the table keeps `machine.ts` free of
 * sync vocabulary, and the `satisfies` clause makes an unmapped outbox-firing event a build error
 * rather than a row that silently enqueues under the wrong trigger.
 */
const OUTBOX_TRIGGER_FOR: Partial<Record<TransitionEvent, OutboxTrigger>> = {
  Half: OUTBOX_TRIGGER.halftime,
  FullTime: OUTBOX_TRIGGER.fullTime,
  Abandon: OUTBOX_TRIGGER.fullTime,
  CompleteDistillation: OUTBOX_TRIGGER.distillComplete,
};

/**
 * §2.2 — `idle → preMatch`.
 *
 * The two guards are `machine`'s, and the second is the one §2.2 spends a paragraph on: a `preMatch`
 * match does **not** block a match from *starting* (SES-9 restricts in-match states only), but it
 * does hold the singleton `activeMatch` pointer, so creating a second one would strand the first as
 * an unreachable row. The rejection is `ERR_SETUP_IN_PROGRESS`, and the UI answers it by offering
 * `DiscardSetup` at the point of refusal — a rejection with a route out, not a deadlock.
 */
export function handleCreateMatch(
  cmd: CreateMatchCommand,
  snap: CreateMatchSnapshot,
): Decision<TransitionOp> {
  const decision = transition(MATCH_STATE.idle, 'CreateMatch', {
    now: snap.now,
    matchId: null,
    facts: {
      anotherMatchIsInMatch: snap.facts.anotherMatchIsInMatch,
      anotherPreMatchExists: snap.facts.anotherPreMatchExists,
      // Not consulted by `CreateMatch`'s guards. Supplied because `TransitionFacts` requires every
      // field: an optional guard fact is a guard that silently does not run.
      halfLengthMin: cmd.row.halfLengthMin,
      unresolvedFlagCount: 0,
      draftCardCount: 0,
      fullTimeTalkPointRanks: [],
      abandoned: cmd.row.abandoned,
      capturePhase: cmd.row.capturePhase,
    },
  });
  if (!decision.ok) return decision;

  return ok(
    {
      ops: [
        { t: 'insertMatch', row: cmd.row },
        { t: 'setActiveMatch', matchId: cmd.row.id },
      ],
      log: [line('info', cmd.row.id, 'match created', { teamId: cmd.row.teamId })],
      post: [],
    },
    decision.log,
  );
}

/**
 * §2.2 — every other transition.
 *
 * @param snap.match `undefined` when the row vanished between dispatch and the transaction. That is
 *   a rejection, not a throw: SES-2 only requires that nothing is acknowledged, and a typed
 *   `{ok:false}` is in the type of every call site where a thrown error would not be.
 */
export function handleTransition(
  cmd: TransitionCommand,
  snap: TransitionSnapshot,
): Decision<TransitionOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.', {
      event: cmd.event,
    });
  }

  const decision = transition(match.state, cmd.event, {
    now: snap.now,
    anchorTs: cmd.anchorTs,
    matchId: cmd.matchId,
    facts: snap.facts,
  });
  if (!decision.ok) return decision;

  const effect = decision.value;
  return ok(
    {
      ops: opsFor(cmd.matchId, effect),
      outbox: effect.outbox ? outboxRow(cmd.matchId, cmd.event, snap.now) : undefined,
      log: [
        line('info', cmd.matchId, `${effect.from} --${cmd.event}--> ${effect.to ?? 'deleted'}`, {
          anchor: effect.anchor?.field ?? null,
          abandoned: effect.setsAbandoned,
        }),
      ],
      post: postFor(cmd.matchId, cmd.event),
      // §13.6 — the INV-2 smoke alarm, at every state transition and nowhere near the tap path.
      driftCheck: { matchId: cmd.matchId },
    },
    decision.log,
  );
}

/** §2.2's side-effect column, as ops. Order is deliberate: the match row settles before the pointer. */
function opsFor(matchId: string, effect: TransitionEffect): TransitionOp[] {
  const ops: TransitionOp[] = [];

  if (effect.deletesMatch) {
    /* INV-8 — a deleting transition cascades every dependent row in this same transaction. Two
       events reach here and there is deliberately only one op: `DiscardSetup` (an unstarted setup)
       and `DeleteMatch` (an abandoned match, ruled 2026-08-19). Whether a delete is *permitted* is
       `machine.transition`'s question; what a delete *does* is this one line for both. */
    ops.push({ t: 'cascadeDeleteMatch', matchId });
  } else if (effect.to !== null) {
    ops.push({
      t: 'transitionMatch',
      id: matchId,
      patch: {
        state: effect.to,
        anchor: effect.anchor
          ? { field: effect.anchor.field, ts: effect.anchor.value }
          : undefined,
        abandoned: effect.setsAbandoned ? true : undefined,
      },
    });
  }

  if (effect.triagePassedTs !== null) {
    ops.push({ t: 'writeTriagePassed', id: matchId, ts: effect.triagePassedTs });
  }

  // §2.5's resume pointer and §13.7's writer heartbeat share one row, so every transition touches it.
  // `unchanged` must NOT re-point: at `EnterTriage` the pointer was already cleared by Full Time, and
  // writing `matchId` back would resurrect a resume target for a match that is no longer active.
  if (effect.activeMatch === 'set') ops.push({ t: 'setActiveMatch', matchId });
  else if (effect.activeMatch === 'clear') ops.push({ t: 'setActiveMatch', matchId: null });
  else ops.push({ t: 'touchWriterHeartbeat' });

  return ops;
}

/** INV-6 — the only aborting postcondition a transition carries, on the only writer of `archived`. */
function postFor(matchId: string, event: TransitionEvent): Postcondition[] {
  return event === 'CompleteDistillation' ? [{ t: 'talkPointSet', matchId }] : [];
}

/**
 * SYN-1's row, enqueued in the transition's own transaction (ENV-9).
 *
 * `attempts: 0` and `state: 'pending'` reset on every trigger, which is what coalescing means here:
 * a second Half→FullTime sequence with an undrained row present overwrites rather than appends, so
 * the transition can never abort on a `ConstraintError` (§3.4).
 */
function outboxRow(matchId: string, event: TransitionEvent, now: number): OutboxRow {
  const trigger = OUTBOX_TRIGGER_FOR[event];
  /* c8 ignore next 3 -- unreachable: every `outbox: true` row in TRANSITION_TABLE is mapped above,
     and `outboxTriggerCoverage` proves it. The throw exists so that adding an outbox-firing event
     without a trigger fails loudly at the call site instead of enqueuing `undefined`. */
  if (trigger === undefined) {
    throw new Error(`SYN-1: no outbox trigger is mapped for the ${event} transition.`);
  }
  return {
    kind: 'match',
    scopeId: matchId,
    trigger,
    enqueuedAt: now,
    attempts: 0,
    lastAttemptAt: null,
    lastError: null,
    state: 'pending',
  };
}

/**
 * Which events map to an outbox trigger — exported so a test can compare it against
 * `TRANSITION_TABLE`'s `outbox` column in both directions rather than trusting this file.
 */
export function outboxTriggerFor(event: TransitionEvent): OutboxTrigger | undefined {
  return OUTBOX_TRIGGER_FOR[event];
}

function line(
  level: DomainLogEntry['level'],
  matchId: string | null,
  msg: string,
  data: Record<string, unknown> = {},
): DomainLogEntry {
  return { module: MODULE, level, matchId, msg, data };
}
